import { useQuery } from '@apollo/client'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
  GET_CATEGORIES,
  toCategoryOptions,
  type IGetCategories,
  type IGetCategoriesVariables,
} from '@commerce/core'

/** The rail asks for every category at once; a storefront has tens, not thousands. */
const CATEGORY_PAGE_SIZE = 100

export interface UseCategoryOptions {
  /** Active top-level category names, in the storefront's authored order. */
  categories: string[]
  loading: boolean
}

/**
 * The chip rail's categories, from the storefront's own list.
 *
 * A failed or empty answer means no category chips — the same quiet degradation
 * `usePromotions` takes, and for the same reason: the rail narrows a grid that
 * renders fine without it, so an outage here must not stand between the shopper
 * and the products. The caller keeps "Todos" and "Promociones" either way,
 * because neither comes from this list.
 */
export const useCategoryOptions = (): UseCategoryOptions => {
  const { slug = '', sessionId = '' } = useParams()

  const { data, loading } = useQuery<IGetCategories, IGetCategoriesVariables>(GET_CATEGORIES, {
    variables: {
      storefrontName: slug,
      sessionUid: sessionId,
      pagination: { pageNumber: 1, pageSize: CATEGORY_PAGE_SIZE },
    },
  })

  const categories = useMemo(() => toCategoryOptions(data?.categories), [data])

  return { categories, loading }
}
